import { config } from '../config/config';
import { supabase } from '../lib/supabase';

// Interface pour les détails de paiement Stripe
export interface StripeElearningPaymentDetails {
  amount: number; // Montant en EUR
  currency: string; // EUR
  order_id: string;
  order_number: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
  return_base_url?: string;
}

// Interface pour la réponse d'initiation de paiement
export interface StripeElearningPaymentResponse {
  order: {
    id: string;
    amount: number;
    status: string;
    payment_method: string;
    payment_reference: string;
    payment_url: string;
  };
  payment_reference: string;
  payment_url: string;
}

// Interface pour la réponse de statut de paiement
export interface StripeElearningStatusResponse {
  status: 'success' | 'pending' | 'failed' | 'paid' | 'completed';
  data: any;
}

/**
 * Initie un paiement Stripe via la fonction Edge spécifique pour l'e-learning
 */
export const initiateStripeElearningEdgePayment = async (
  details: StripeElearningPaymentDetails
): Promise<StripeElearningPaymentResponse> => {
  try {
    console.log('[StripeElearningEdge] Initiation du paiement Stripe:', details);

    // Utiliser la fonction edge spécifique pour l'e-learning
    const response = await fetch(`${config.supabaseUrl}/functions/v1/elearning-stripe-initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify(details),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[StripeElearningEdge] Erreur de la fonction edge:', errorData);
      throw new Error(errorData.error || "Échec de l'initiation du paiement Stripe");
    }

    const data = await response.json();
    console.log('[StripeElearningEdge] Paiement Stripe initié avec succès:', data);
    return data;
  } catch (error) {
    console.error("[StripeElearningEdge] Erreur lors de l'initiation du paiement:", error);
    throw error;
  }
};

/**
 * Vérifie le statut d'un paiement Stripe via la base de données pour l'e-learning
 */
export const checkStripeElearningEdgePaymentStatus = async (
  reference: string,
  order_id: string
): Promise<StripeElearningStatusResponse> => {
  const requestId = Math.random().toString(36).substring(2, 10);
  console.log(`[StripeElearningEdge][${requestId}] Vérification du statut - reference: ${reference}, order_id: ${order_id}`);
  
  try {
    // Vérifier le statut de la commande dans la base de données
    console.log(`[StripeElearningEdge][${requestId}] Vérification du statut dans la base de données`);
    
    const startTime = Date.now();
    const { data: order, error } = await supabase
      .from('elearn_orders')
      .select('status, payment_reference, created_at')
      .eq('id', order_id)
      .single();
    
    const queryTime = Date.now() - startTime;
    console.log(`[StripeElearningEdge][${requestId}] Requête terminée en ${queryTime}ms`);
    console.log(`[StripeElearningEdge][${requestId}] Résultat:`, { 
      order: order ? {
        status: order.status,
        payment_reference: order.payment_reference,
        created_at: order.created_at
      } : null, 
      error: error ? error.message : null 
    });
    
    if (error) {
      console.error(`[StripeElearningEdge][${requestId}] Erreur lors de la récupération:`, error);
      throw new Error(`Erreur lors de la récupération de la commande: ${error.message}`);
    }
    
    if (!order) {
      console.error(`[StripeElearningEdge][${requestId}] Commande ${order_id} introuvable`);
      throw new Error('Commande introuvable');
    }
    
    console.log(`[StripeElearningEdge][${requestId}] Statut de la commande: ${order.status}`);
    
    // Vérifier si la commande est marquée comme payée
    if (order.status === 'completed') {
      console.log(`[StripeElearningEdge][${requestId}] Commande déjà payée, retour immédiat`);
      return { 
        status: 'success', 
        data: { 
          order_id,
          payment_reference: reference,
          status: 'completed',
          created_at: order.created_at
        } 
      };
    }
    
    console.log(`[StripeElearningEdge][${requestId}] Commande en attente (statut: ${order.status})`);
    
    // Retourner le statut actuel
    return {
      status: order.status === 'pending_payment' ? 'pending' : 'failed',
      data: {
        order_id,
        payment_reference: reference,
        status: order.status,
        created_at: order.created_at
      }
    };
  } catch (error) {
    console.error(`[StripeElearningEdge][${requestId}] Erreur lors de la vérification:`, error);
    throw error;
  }
};
